import { Button, FormInput, FormRoot, RHF, zodResolver } from "@devhop/ui";
import { CarIcon } from "lucide-react";
import { useMemo } from "react";
import { z } from "zod";
import type {
  VehicleConfigurationCreateInput,
  VehicleConfigurationUpdateInput,
} from "@/modules/catalog/domain/contracts";

const VehicleConfigurationFormSchema = z.object({
  makeId: z.string().uuid("ກະລຸນາເລືອກຍີ່ຫໍ້"),
  modelId: z.string().uuid("ກະລຸນາເລືອກຮຸ່ນ"),
  modelCode: z.string().max(30).optional(),
  yearFrom: z.string().optional(),
  monthFrom: z.string().optional(),
  yearTo: z.string().optional(),
  monthTo: z.string().optional(),
  engine: z.string().optional(),
  marketCode: z.string().max(20).optional(),
  transmissionCode: z.string().max(50).optional(),
  engineCode: z.string().max(50).optional(),
  gradeCode: z.string().max(50).optional(),
  adminNotes: z.string().optional(),
});

type VehicleConfigurationFormValues = z.infer<
  typeof VehicleConfigurationFormSchema
>;

type VehicleConfigurationFormProps = {
  initialValues?: Partial<VehicleConfigurationFormValues>;
  makeOptions: Array<{ value: string; label: string }>;
  modelOptions: Array<{ value: string; label: string; makeId: string }>;
  onSubmit: (
    values: VehicleConfigurationCreateInput | VehicleConfigurationUpdateInput,
  ) => void;
  onCancel?: () => void;
  submitting?: boolean;
  submitLabel?: string;
};

function parseOptionalInt(value: string | undefined): number | null {
  if (!value || value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : Math.trunc(parsed);
}

function parseOptionalMonth(value: string | undefined): number | null {
  const parsed = parseOptionalInt(value);
  if (parsed == null) return null;
  if (parsed < 1 || parsed > 12) return null;
  return parsed;
}

function emptyToNull(value: string | undefined): string | null {
  return value?.trim() ? value.trim() : null;
}

export function VehicleConfigurationForm({
  initialValues,
  makeOptions,
  modelOptions,
  onSubmit,
  onCancel,
  submitting,
  submitLabel = "ບັນທຶກ",
}: VehicleConfigurationFormProps) {
  const methods = RHF.useForm<VehicleConfigurationFormValues>({
    resolver: zodResolver(VehicleConfigurationFormSchema),
    defaultValues: {
      makeId: initialValues?.makeId ?? makeOptions[0]?.value ?? "",
      modelId: initialValues?.modelId ?? "",
      modelCode: initialValues?.modelCode ?? "",
      yearFrom: initialValues?.yearFrom ?? "",
      monthFrom: initialValues?.monthFrom ?? "",
      yearTo: initialValues?.yearTo ?? "",
      monthTo: initialValues?.monthTo ?? "",
      engine: initialValues?.engine ?? "",
      marketCode: initialValues?.marketCode ?? "",
      transmissionCode: initialValues?.transmissionCode ?? "",
      engineCode: initialValues?.engineCode ?? "",
      gradeCode: initialValues?.gradeCode ?? "",
      adminNotes: initialValues?.adminNotes ?? "",
    },
  });

  const makeId = RHF.useWatch({ control: methods.control, name: "makeId" });
  const modelId = RHF.useWatch({ control: methods.control, name: "modelId" });
  const makeLabel = useMemo(
    () => makeOptions.find((o) => o.value === makeId)?.label ?? "—",
    [makeOptions, makeId],
  );
  const modelLabel = useMemo(
    () => modelOptions.find((m) => m.value === modelId)?.label ?? "—",
    [modelOptions, modelId],
  );

  return (
    <FormRoot<VehicleConfigurationFormValues>
      methods={methods}
      onSubmit={(vals) =>
        onSubmit({
          modelId: vals.modelId,
          modelCode: emptyToNull(vals.modelCode),
          yearFrom: parseOptionalInt(vals.yearFrom),
          yearTo: parseOptionalInt(vals.yearTo),
          monthFrom: parseOptionalMonth(vals.monthFrom),
          monthTo: parseOptionalMonth(vals.monthTo),
          engine: emptyToNull(vals.engine),
          marketCode: emptyToNull(vals.marketCode),
          transmissionCode: emptyToNull(vals.transmissionCode),
          engineCode: emptyToNull(vals.engineCode),
          gradeCode: emptyToNull(vals.gradeCode),
          adminNotes: emptyToNull(vals.adminNotes),
        })
      }
      className="space-y-4"
    >
      <input type="hidden" {...methods.register("makeId")} />
      <input type="hidden" {...methods.register("modelId")} />
      <div
        className="flex items-start gap-3 rounded-xl border border-border/80 bg-muted/20 py-3 pr-4 pl-4"
        style={{ borderLeftWidth: "4px", borderLeftColor: "#004799" }}
      >
        <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-[#004799]/10 text-[#004799]">
          <CarIcon className="size-4" aria-hidden />
        </div>
        <div className="min-w-0 flex-1 space-y-1">
          <span className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
            ຍີ່ຫໍ້ລົດ · ຮຸ່ນ
          </span>
          <p className="font-semibold text-foreground tracking-tight">
            {makeLabel}
            <span className="mx-2 font-normal text-muted-foreground">—</span>
            {modelLabel}
          </p>
        </div>
      </div>
      <FormInput
        name="modelCode"
        label="Model code (OEM platform)"
        placeholder="NCP150, GK5"
      />
      <div className="grid gap-3 md:grid-cols-2">
        <FormInput name="yearFrom" label="ປີເລີ່ມ" placeholder="ຕົວຢ່າງ: 2018" />
        <FormInput
          name="monthFrom"
          label="ເດືອນເລີ່ມ"
          placeholder="1–12 (ບໍ່ບັງຄັບ)"
        />
        <FormInput name="yearTo" label="ປີສິ້ນສຸດ" placeholder="ຕົວຢ່າງ: 2022" />
        <FormInput
          name="monthTo"
          label="ເດືອນສິ້ນສຸດ"
          placeholder="1–12 (ບໍ່ບັງຄັບ)"
        />
      </div>
      <FormInput name="engine" label="Engine" placeholder="ຕົວຢ່າງ: 1.5L" />
      <div className="grid gap-3 md:grid-cols-2">
        <FormInput name="marketCode" label="Market code" placeholder="LA" />
        <FormInput
          name="transmissionCode"
          label="Transmission code"
          placeholder="CVT"
        />
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        <FormInput name="engineCode" label="Engine code" placeholder="2NR" />
        <FormInput name="gradeCode" label="Grade code" placeholder="G" />
      </div>
      <FormInput
        name="adminNotes"
        label="ໝາຍເຫດ (admin)"
        placeholder="ລາຍລະອຽດສຳລັບ admin"
      />
      <div className="flex justify-end gap-2 pt-2">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            ຍົກເລີກ
          </Button>
        )}
        <Button type="submit" disabled={submitting}>
          {submitLabel}
        </Button>
      </div>
    </FormRoot>
  );
}
